import React, { useState, useEffect, useRef } from 'react';
import * as XLSX from 'xlsx';
import api from '../services/api';
import { PERIOD_PRESETS, computeDateRange } from '../utils/jdmaPeriod';
import './DynamicDB.css';

function DynamicDB({ apiPath, title, templateConfig, sftpSync = false, visibleColumns = null, primaryKeyLabel = 'IMMAT' }) {
  const [entries, setEntries]       = useState([]);
  const [columns, setColumns]       = useState([]); // colonnes dynamiques (hors immat)
  const [concessionPivotKey, setConcessionPivotKey] = useState(null); // clé `data` utilisée pour le mapping concession
  const [periodPivotKey, setPeriodPivotKey] = useState(null); // clé `data` utilisée pour la sync par période
  const [loading, setLoading]       = useState(true);
  const [filePreview, setFilePreview] = useState(null); // { rows, extraColumns, filename }
  const [importing, setImporting]   = useState(false);
  const [syncing, setSyncing]       = useState(false);
  const [lastImport, setLastImport] = useState(null);  // { count, date }
  const [search, setSearch]         = useState('');
  const [filtreConc, setFiltreConc] = useState('');
  const [toast, setToast]           = useState(null);
  const fileInputRef = useRef(null);
  const [showImportChoice, setShowImportChoice] = useState(false);
  const [showSyncModal, setShowSyncModal] = useState(false);
  const [syncPeriod, setSyncPeriod] = useState({ preset: '30d', from: '', to: '' });

  // Colonnes dynamiques affichées : on retire la clé pivot des colonnes
  // de droite quand on affiche une colonne « Concession » dédiée (le code
  // brut reste accessible en tooltip).
  const displayCols = (visibleColumns
    ? visibleColumns.filter(c => columns.includes(c))
    : columns
  ).filter(c => !concessionPivotKey || c !== concessionPivotKey);
  const showPrimaryKey = !visibleColumns || visibleColumns.includes('immat');

  // Liste des concessions distinctes présentes dans les données (pour le filtre)
  const concessionsList = (() => {
    if (!concessionPivotKey) return [];
    const map = new Map();
    for (const e of entries) {
      if (e.concession_id && e.concession_nom && !map.has(e.concession_id)) {
        map.set(e.concession_id, e.concession_nom);
      }
    }
    return Array.from(map.entries())
      .map(([id, nom]) => ({ id, nom }))
      .sort((a, b) => a.nom.localeCompare(b.nom));
  })();

  const load = async () => {
    try {
      setLoading(true);
      const res = await api.get(apiPath);
      setEntries(res.data.entries);
      setColumns(res.data.columns);
      setConcessionPivotKey(res.data.concessionPivotKey || null);
      setPeriodPivotKey(res.data.periodPivotKey || null);
    } catch (err) {
      console.error(`Erreur chargement ${apiPath}:`, err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  };

  const downloadTemplate = () => {
    if (!templateConfig) return;
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet([
      templateConfig.headers,
      templateConfig.exampleRow,
    ]);
    XLSX.utils.book_append_sheet(wb, ws, 'Modèle');
    XLSX.writeFile(wb, templateConfig.filename);
  };

  /* ── Parsing Excel ── */
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const data = new Uint8Array(evt.target.result);
        const wb   = XLSX.read(data, { type: 'array' });
        const ws   = wb.Sheets[wb.SheetNames[0]];
        const json = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });

        if (json.length < 2) { showToast('Fichier vide ou sans données', 'error'); return; }

        const rawHeaders = json[0].map(h => (h || '').toString().trim());

        // Détection colonne IMMAT
        const immatIdx = rawHeaders.findIndex(h =>
          ['immat', 'plaque', 'immatriculation'].some(k => h.toLowerCase().includes(k))
        );
        if (immatIdx === -1) {
          showToast('Colonne immat/plaque/immatriculation introuvable', 'error');
          return;
        }

        // Toutes les autres colonnes
        const extraColumns = rawHeaders.filter((_, i) => i !== immatIdx);

        const rows = json.slice(1).map(row => {
          const obj = { immat: (row[immatIdx] || '').toString().trim() };
          rawHeaders.forEach((h, i) => {
            if (i !== immatIdx) obj[h] = (row[i] || '').toString().trim();
          });
          return obj;
        }).filter(r => r.immat);

        setFilePreview({ rows, extraColumns, immatHeader: rawHeaders[immatIdx], filename: file.name });
      } catch {
        showToast('Erreur de lecture du fichier', 'error');
      }
    };
    reader.readAsArrayBuffer(file);
    e.target.value = '';
  };

  const confirmImport = async () => {
    if (!filePreview) return;
    try {
      setImporting(true);
      const allColumns = ['immat', ...filePreview.extraColumns];
      const res = await api.post(`${apiPath}/import`, {
        rows: filePreview.rows,
        columns: allColumns,
      });
      const count = res.data.imported;
      setLastImport({ count, date: new Date().toLocaleString('fr-FR') });
      setFilePreview(null);
      await load();
      showToast(`${count} ligne${count !== 1 ? 's' : ''} importée${count !== 1 ? 's' : ''} avec succès`);
    } catch (err) {
      showToast(err.response?.data?.error || "Erreur lors de l'import", 'error');
    } finally {
      setImporting(false);
    }
  };

  // Lancement direct quand la table n'a pas de pivot période (mode legacy)
  // ou via la modale (mode période).
  const runSync = async (period = null) => {
    try {
      setSyncing(true);
      let body = {};
      let label = 'depuis le SFTP';
      if (period && period.preset !== 'all') {
        const { from, to } = computeDateRange(period);
        body = { from, to };
        const presetLabel = PERIOD_PRESETS.find(p => p.value === period.preset)?.label || period.preset;
        label = `pour la période « ${presetLabel} »`;
      }
      const res = await api.post(`${apiPath}/sync`, body);
      const { imported, mode, totalRows, filteredOutByPeriod } = res.data;
      setLastImport({ count: imported, date: new Date().toLocaleString('fr-FR') });
      await load();
      if (mode === 'period') {
        showToast(
          `${imported} ligne${imported !== 1 ? 's' : ''} synchronisée${imported !== 1 ? 's' : ''} ${label}. ${filteredOutByPeriod} ligne${filteredOutByPeriod !== 1 ? 's' : ''} hors période ignorée${filteredOutByPeriod !== 1 ? 's' : ''} sur ${totalRows} au total.`
        );
      } else {
        showToast(`${imported} ligne${imported !== 1 ? 's' : ''} synchronisée${imported !== 1 ? 's' : ''} ${label} (sync complète)`);
      }
    } catch (err) {
      showToast(err.response?.data?.error || 'Erreur lors de la synchronisation SFTP', 'error');
    } finally {
      setSyncing(false);
    }
  };

  const onSyncClick = () => {
    if (periodPivotKey) {
      setShowSyncModal(true);
    } else {
      runSync(null);
    }
  };

  const confirmSyncWithPeriod = async () => {
    if (syncPeriod.preset === 'custom' && (!syncPeriod.from || !syncPeriod.to)) {
      showToast('Veuillez renseigner les deux dates pour la période personnalisée', 'error');
      return;
    }
    setShowSyncModal(false);
    await runSync(syncPeriod);
  };

  /* ── Filtrage ── */
  const filteredEntries = entries.filter(e => {
    if (filtreConc && String(e.concession_id) !== String(filtreConc)) return false;
    if (!search) return true;
    const q = search.toLowerCase();
    if (e.immat?.toLowerCase().includes(q)) return true;
    if (e.concession_nom?.toLowerCase().includes(q)) return true;
    if (e.data && typeof e.data === 'object') {
      return Object.values(e.data).some(v =>
        v !== null && v !== undefined && v.toString().toLowerCase().includes(q)
      );
    }
    return false;
  });

  const formatDate = (s) => {
    if (!s) return '-';
    try { return new Date(s).toLocaleDateString('fr-FR'); } catch { return s; }
  };

  const getCellValue = (entry, col) => {
    if (!entry.data) return '-';
    const v = entry.data[col];
    return (v !== null && v !== undefined && v !== '') ? v : '-';
  };

  /* ── Rendu ── */
  return (
    <div className="ddb-container">
      {toast && <div className={`ddb-toast ${toast.type}`}>{toast.msg}</div>}

      {/* Toolbar */}
      <div className="ddb-toolbar">
        {sftpSync && (
          <button className="btn-sync-ddb" onClick={onSyncClick} disabled={syncing}>
            {syncing ? '⏳ Synchronisation…' : '🔄 Synchroniser depuis SFTP'}
          </button>
        )}
        <button className="btn-import-ddb" onClick={() => setShowImportChoice(true)}>
          📥 Importer un fichier Excel
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx,.xls,.csv"
          style={{ display: 'none' }}
          onChange={handleFileChange}
        />
        <input
          type="text"
          placeholder="Rechercher..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="ddb-search"
        />
        {search && (
          <button className="btn-clear-ddb" onClick={() => setSearch('')}>Effacer</button>
        )}
        {concessionPivotKey && concessionsList.length > 1 && (
          <select
            className="ddb-search"
            value={filtreConc}
            onChange={(e) => setFiltreConc(e.target.value)}
            title="Filtrer par concession"
          >
            <option value="">Toutes concessions</option>
            {concessionsList.map(c => {
              const nb = entries.filter(e => String(e.concession_id) === String(c.id)).length;
              return <option key={c.id} value={c.id}>{c.nom} ({nb})</option>;
            })}
          </select>
        )}
        <span className="ddb-count">
          {filteredEntries.length} / {entries.length} ligne{entries.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Dernier import */}
      {lastImport && (
        <div className="ddb-last-import">
          Dernier import : {lastImport.count} ligne{lastImport.count !== 1 ? 's' : ''} — {lastImport.date}
        </div>
      )}

      {/* Aperçu avant import */}
      {filePreview && (
        <div className="ddb-preview">
          <div className="preview-header">
            <strong>📄 {filePreview.filename}</strong>
            <span className="preview-count">
              {filePreview.rows.length} ligne{filePreview.rows.length !== 1 ? 's' : ''} détectée{filePreview.rows.length !== 1 ? 's' : ''}
            </span>
            <span className="preview-cols">
              {filePreview.extraColumns.length + 1} colonne{filePreview.extraColumns.length + 1 !== 1 ? 's' : ''} :
              {' '}IMMAT{filePreview.extraColumns.length > 0 ? ', ' + filePreview.extraColumns.join(', ') : ''}
            </span>
          </div>
          <div className="preview-actions">
            <button className="btn-secondary-ddb" onClick={() => setFilePreview(null)}>Annuler</button>
            <button className="btn-primary-ddb" onClick={confirmImport} disabled={importing}>
              {importing ? 'Import en cours...' : `✅ Confirmer l'import (${filePreview.rows.length} lignes)`}
            </button>
          </div>
          <div className="preview-table-wrapper">
            <table className="preview-table-ddb">
              <thead>
                <tr>
                  <th>IMMAT</th>
                  {filePreview.extraColumns.map(c => <th key={c}>{c}</th>)}
                </tr>
              </thead>
              <tbody>
                {filePreview.rows.slice(0, 10).map((r, i) => (
                  <tr key={i}>
                    <td><span className="plaque-badge">{r.immat}</span></td>
                    {filePreview.extraColumns.map(c => <td key={c}>{r[c] || '-'}</td>)}
                  </tr>
                ))}
                {filePreview.rows.length > 10 && (
                  <tr>
                    <td colSpan={1 + filePreview.extraColumns.length} className="preview-more-ddb">
                      … et {filePreview.rows.length - 10} ligne{filePreview.rows.length - 10 !== 1 ? 's' : ''} supplémentaire{filePreview.rows.length - 10 !== 1 ? 's' : ''}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Tableau principal */}
      {loading ? (
        <div className="ddb-loading">Chargement...</div>
      ) : (
        <div className="ddb-table-wrapper">
          <table className="ddb-table">
            <thead>
              <tr>
                {showPrimaryKey && <th>{primaryKeyLabel}</th>}
                {concessionPivotKey && <th>Concession</th>}
                {displayCols.map(c => <th key={c}>{c}</th>)}
                <th>Date import</th>
              </tr>
            </thead>
            <tbody>
              {filteredEntries.length === 0 ? (
                <tr>
                  <td colSpan={displayCols.length + (showPrimaryKey ? 1 : 0) + (concessionPivotKey ? 1 : 0) + 1} className="ddb-empty">
                    {entries.length === 0
                      ? `Aucune donnée. Importez un fichier Excel pour commencer.`
                      : 'Aucun résultat pour cette recherche.'}
                  </td>
                </tr>
              ) : filteredEntries.map(entry => {
                const rawPivot = concessionPivotKey ? (entry.data?.[concessionPivotKey] ?? '') : '';
                return (
                  <tr key={entry.id}>
                    {showPrimaryKey && <td><span className="plaque-badge">{entry.immat}</span></td>}
                    {concessionPivotKey && (
                      <td title={rawPivot ? `Code source : ${rawPivot}` : undefined}>
                        {entry.concession_nom
                          ? entry.concession_nom
                          : <span className="ddb-conc-unmapped">{rawPivot || '—'}</span>}
                      </td>
                    )}
                    {displayCols.map(c => <td key={c}>{getCellValue(entry, c)}</td>)}
                    <td className="ddb-date">{formatDate(entry.created_at)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {/* ── Modal choix période sync ── */}
      {showSyncModal && (
        <div className="import-choice-overlay" onClick={() => setShowSyncModal(false)}>
          <div className="import-choice-modal ddb-sync-modal" onClick={e => e.stopPropagation()}>
            <div className="import-choice-header">
              <h3>🔄 Synchroniser depuis SFTP</h3>
              <button className="import-choice-close" onClick={() => setShowSyncModal(false)}>×</button>
            </div>
            <div className="ddb-sync-body">
              <p className="ddb-sync-intro">
                Choisissez la période à synchroniser (filtre sur <strong>{periodPivotKey}</strong>).
                Les dossiers déjà en base hors période ne sont <strong>pas touchés</strong>.
              </p>
              <label className="ddb-sync-label">Période</label>
              <select
                className="ddb-search"
                value={syncPeriod.preset}
                onChange={(e) => setSyncPeriod(p => ({ ...p, preset: e.target.value }))}
              >
                {PERIOD_PRESETS.map(p => (
                  <option key={p.value} value={p.value}>{p.label}</option>
                ))}
                <option value="all">⚠️ Tout (écrase toute la table)</option>
              </select>
              {syncPeriod.preset === 'custom' && (
                <div className="ddb-sync-custom">
                  <label>Du <input type="date" value={syncPeriod.from} onChange={(e) => setSyncPeriod(p => ({ ...p, from: e.target.value }))} /></label>
                  <label>Au <input type="date" value={syncPeriod.to} onChange={(e) => setSyncPeriod(p => ({ ...p, to: e.target.value }))} /></label>
                </div>
              )}
              {syncPeriod.preset === 'all' && (
                <div className="ddb-sync-warn">
                  ⚠️ Le mode « Tout » <strong>efface toutes les lignes existantes</strong> avant de réimporter l'intégralité du fichier SFTP. À utiliser uniquement pour une remise à zéro complète.
                </div>
              )}
            </div>
            <div className="ddb-sync-actions">
              <button className="btn-secondary-ddb" onClick={() => setShowSyncModal(false)}>Annuler</button>
              <button className="btn-primary-ddb" onClick={confirmSyncWithPeriod} disabled={syncing}>
                {syncing ? '⏳ Synchronisation…' : '✅ Lancer la synchronisation'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal choix import ── */}
      {showImportChoice && (
        <div className="import-choice-overlay" onClick={() => setShowImportChoice(false)}>
          <div className="import-choice-modal" onClick={e => e.stopPropagation()}>
            <div className="import-choice-header">
              <h3>📥 Importer un fichier Excel</h3>
              <button className="import-choice-close" onClick={() => setShowImportChoice(false)}>×</button>
            </div>
            <div className="import-choice-body">
              <button
                className="import-choice-btn btn-download"
                onClick={downloadTemplate}
                disabled={!templateConfig}
                title={!templateConfig ? 'Modèle non disponible' : undefined}
              >
                <span className="choice-icon">📄</span>
                <span>Télécharger<br />le fichier modèle</span>
              </button>
              <button
                className="import-choice-btn btn-upload"
                onClick={() => { setShowImportChoice(false); fileInputRef.current?.click(); }}
              >
                <span className="choice-icon">📂</span>
                <span>Charger<br />le fichier</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DynamicDB;
